/**
 * 手機／email 正規化與雜湊（「第一次參與」同名異人判斷用）。
 *
 * 前端（utils/firstTimeParticipant.ts）與腳本（scripts/enrich-record-contacts.mjs、
 * scripts/lib/church-roster.mjs）共用這一份，確保兩邊算出的雜湊一致。
 *
 * - 手機：只留數字；+886／00886／886 開頭改成 0；9 碼且 9 開頭補 0；
 *   超過 10 碼且結尾是 09xxxxxxxx（例：+886 0912…）取最後 10 碼；不足 9 碼視為無效。
 * - email：去空白、轉小寫，需符合基本格式。
 * - 雜湊：SHA-256 hex，輸入為 `p:<手機>` 或 `e:<email>`。
 *
 * 紀錄（trip_participants／historical_records）只存雜湊 `contactHashes`，不存原始值。
 * SHA-256 用同步的純 TS 實作，讓前端索引可以留在 useMemo 裡。
 */

// 一個欄位填多支電話／多個 email 時的分隔
const MULTI_VALUE_SPLIT_RE = /[/／,，;；、|]|\s+or\s+/i;

/** 單一電話字串 → 正規化數字；無效回傳 ''。 */
export const normalizePhone = (value: unknown): string => {
  let digits = String(value ?? '').replace(/\D/g, '');
  if (digits.startsWith('00886')) digits = digits.slice(2);
  if (digits.startsWith('886')) digits = `0${digits.slice(3)}`;
  if (digits.length === 9 && digits.startsWith('9')) digits = `0${digits}`;
  if (digits.length > 10) {
    const mobileTail = digits.match(/09\d{8}$/);
    if (mobileTail) digits = mobileTail[0];
  }
  return digits.length >= 9 ? digits : '';
};

/** 單一 email 字串 → 小寫去空白；無效回傳 ''。 */
export const normalizeEmail = (value: unknown): string => {
  const email = String(value ?? '').trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '';
};

const splitValues = (value: unknown): string[] =>
  String(value ?? '').split(MULTI_VALUE_SPLIT_RE).map(part => part.trim()).filter(Boolean);

/** 聯絡資料 → 正規化 key（`p:0912345678`、`e:a@b.tw`），去重。 */
export const getContactKeys = (person: { phone?: unknown; email?: unknown } | null | undefined): string[] => {
  const keys = new Set<string>();
  splitValues(person?.phone).forEach(part => {
    const phone = normalizePhone(part);
    if (phone) keys.add(`p:${phone}`);
  });
  splitValues(person?.email).forEach(part => {
    const email = normalizeEmail(part);
    if (email) keys.add(`e:${email}`);
  });
  return Array.from(keys);
};

// ---- SHA-256（FIPS 180-4），同步、純 TS ----

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

/** SHA-256 of the UTF-8 bytes of `input`, lowercase hex. */
export const sha256Hex = (input: string): string => {
  const bytes = new TextEncoder().encode(input);
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
  const buffer = new Uint8Array(paddedLength);
  buffer.set(bytes);
  buffer[bytes.length] = 0x80;
  const view = new DataView(buffer.buffer);
  const bitLength = bytes.length * 8;
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000));
  view.setUint32(paddedLength - 4, bitLength >>> 0);

  const hash = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);

  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let a = hash[0], b = hash[1], c = hash[2], d = hash[3];
    let e = hash[4], f = hash[5], g = hash[6], h = hash[7];
    for (let i = 0; i < 64; i++) {
      const t1 = (h + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i] + w[i]) >>> 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    hash[0] += a; hash[1] += b; hash[2] += c; hash[3] += d;
    hash[4] += e; hash[5] += f; hash[6] += g; hash[7] += h;
  }

  let hex = '';
  hash.forEach(word => { hex += word.toString(16).padStart(8, '0'); });
  return hex;
};

const hashCache = new Map<string, string>();

/** 正規化 key（getContactKeys 的輸出）→ 雜湊；有快取。 */
export const hashContactKey = (key: string): string => {
  let hashed = hashCache.get(key);
  if (!hashed) {
    hashed = sha256Hex(key);
    hashCache.set(key, hashed);
  }
  return hashed;
};

/** 聯絡資料 → 雜湊陣列（排序、去重），即 `contactHashes` 欄位的內容。 */
export const getContactHashes = (person: { phone?: unknown; email?: unknown } | null | undefined): string[] =>
  Array.from(new Set(getContactKeys(person).map(hashContactKey))).sort();
